import { FleetWorkshopKind, formatCentAsEuro } from '@gb-ticket/shared-types';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiRequestError } from '@/lib/api';
import { FLEET_WORKSHOP_KIND_LABELS, formatDeDate } from '@/products/fleet/features/fleet-labels';
import {
  fleetPanelClassName,
  fleetTableFrameClassName,
} from '@/products/fleet/features/fleet-panel';
import {
  useCreateFleetWorkshopVisit,
  useFleetWorkshopVisits,
} from '@/products/fleet/features/use-fleet';

export function FleetVehicleWorkshopTab({ vehicleId }: { vehicleId: string }) {
  const visits = useFleetWorkshopVisits(vehicleId);
  const create = useCreateFleetWorkshopVisit();
  const [kind, setKind] = useState<'service' | 'repair' | 'inspection' | 'other'>('service');
  const [workshopName, setWorkshopName] = useState('');
  const [performedOn, setPerformedOn] = useState('');
  const [odometerKm, setOdometerKm] = useState('');
  const [summary, setSummary] = useState('');
  const [costEuro, setCostEuro] = useState('');

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const normalizedCost = costEuro.trim().replace(',', '.');
    const costCents =
      normalizedCost.length > 0 ? Math.round(Number.parseFloat(normalizedCost) * 100) : undefined;
    try {
      await create.mutateAsync({
        vehicleId,
        kind,
        workshopName: workshopName.trim(),
        performedOn,
        odometerKm: odometerKm.trim() ? Number(odometerKm) : undefined,
        summary: summary.trim(),
        costCents: costCents != null && !Number.isNaN(costCents) ? costCents : undefined,
      });
      setWorkshopName('');
      setPerformedOn('');
      setOdometerKm('');
      setSummary('');
      setCostEuro('');
      toast.success('Werkstattbesuch gespeichert.');
    } catch (error: unknown) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  return (
    <div className="space-y-6">
      {visits.data && visits.data.items.length > 0 ? (
        <ul className={`${fleetTableFrameClassName} divide-y text-sm`}>
          {visits.data.items.map((row) => (
            <li key={row.id} className="space-y-1 p-3">
              <p className="font-medium">
                {formatDeDate(row.performedOn)} · {FLEET_WORKSHOP_KIND_LABELS[row.kind]} ·{' '}
                {row.workshopName}
              </p>
              <p className="text-muted-foreground">{row.summary}</p>
              {row.costCents != null ? (
                <p className="text-muted-foreground">Kosten: {formatCentAsEuro(row.costCents)}</p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Noch keine Werkstattbesuche.</p>
      )}

      <form className={`${fleetPanelClassName} space-y-3`} onSubmit={onSubmit}>
        <h3 className="text-base font-semibold">Besuch erfassen</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="ws-kind">Art</Label>
            <Select
              value={kind}
              onValueChange={(value) =>
                setKind(value as 'service' | 'repair' | 'inspection' | 'other')
              }
            >
              <SelectTrigger id="ws-kind" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FleetWorkshopKind.SERVICE}>Service</SelectItem>
                <SelectItem value={FleetWorkshopKind.REPAIR}>Reparatur</SelectItem>
                <SelectItem value={FleetWorkshopKind.INSPECTION}>Prüfung</SelectItem>
                <SelectItem value={FleetWorkshopKind.OTHER}>Sonstiges</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ws-date">Datum</Label>
            <Input
              id="ws-date"
              type="date"
              value={performedOn}
              onChange={(event) => setPerformedOn(event.target.value)}
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ws-name">Werkstatt</Label>
            <Input
              id="ws-name"
              value={workshopName}
              onChange={(event) => setWorkshopName(event.target.value)}
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ws-km">Kilometerstand (optional)</Label>
            <Input
              id="ws-km"
              type="number"
              min={0}
              value={odometerKm}
              onChange={(event) => setOdometerKm(event.target.value)}
            />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="ws-summary">Zusammenfassung</Label>
            <Input
              id="ws-summary"
              value={summary}
              onChange={(event) => setSummary(event.target.value)}
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ws-cost">Kosten in Euro (optional)</Label>
            <Input
              id="ws-cost"
              inputMode="decimal"
              placeholder="12,50"
              value={costEuro}
              onChange={(event) => setCostEuro(event.target.value)}
            />
          </div>
        </div>
        <Button type="submit" disabled={create.isPending}>
          Speichern
        </Button>
      </form>
    </div>
  );
}
